import React, { useEffect, useState } from 'react';
import { FaEdit, FaPlus, FaTrash } from 'react-icons/fa';
import { adminCampaignService } from '../services';
import { resolveMediaUrl } from '../services/api';
import { notifyError, notifyInfo, notifySuccess } from '../utils/toast';

const Campaigns = () => {
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [error, setError] = useState('');
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        goal_amount: '',
        current_amount: '',
        start_date: '',
        end_date: '',
        status: 'active',
        image: null,
    });

    useEffect(() => {
        fetchCampaigns();
    }, []);

    const fetchCampaigns = async () => {
        try {
            const response = await adminCampaignService.getAll();
            setCampaigns(response.data.data || []);
        } catch (err) {
            console.error('Error fetching campaigns:', err);
            notifyError('Unable to load campaigns.');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, files } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'file' ? files?.[0] || null : value,
        }));
    };

    const resetForm = () => {
        setFormData({
            title: '',
            description: '',
            goal_amount: '',
            current_amount: '',
            start_date: '',
            end_date: '',
            status: 'active',
            image: null,
        });
        setEditingId(null);
        setShowForm(false);
        setError('');
    };

    const handleEdit = (campaign) => {
        setFormData({
            title: campaign.title,
            description: campaign.description,
            goal_amount: campaign.goal_amount,
            current_amount: campaign.current_amount,
            start_date: campaign.start_date,
            end_date: campaign.end_date,
            status: campaign.status,
            image: null,
        });
        setEditingId(campaign.id);
        setShowForm(true);
        notifyInfo('Campaign loaded for editing.');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        try {
            if (editingId) {
                await adminCampaignService.update(editingId, formData);
                notifySuccess('Campaign updated successfully.');
            } else {
                await adminCampaignService.create(formData);
                notifySuccess('Campaign created successfully.');
            }
            await fetchCampaigns();
            resetForm();
        } catch (err) {
            setError(err.response?.data?.message || 'Unable to save campaign.');
            notifyError(err.response?.data?.message || 'Unable to save campaign.');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this campaign?')) return;
        try {
            await adminCampaignService.delete(id);
            await fetchCampaigns();
            notifySuccess('Campaign deleted successfully.');
        } catch (err) {
            console.error('Error deleting campaign:', err);
            notifyError('Unable to delete campaign.');
        }
    };

    return (
        <div>
            <div className="mb-8 flex items-center justify-between">
                <h1 className="text-4xl font-bold text-gray-800">Donation Campaigns</h1>
                <button
                    onClick={() => setShowForm(!showForm)}
                    className="flex items-center gap-2 rounded-lg bg-emerald-600 px-6 py-2 text-white transition hover:bg-emerald-700"
                >
                    <FaPlus /> Add Campaign
                </button>
            </div>

            {showForm && (
                <div className="mb-8 rounded-lg bg-white p-8 shadow-lg">
                    <h2 className="mb-6 text-2xl font-bold text-gray-800">{editingId ? 'Edit Campaign' : 'New Campaign'}</h2>
                    {error && <div className="mb-4 rounded-lg bg-red-100 p-3 text-red-700">{error}</div>}
                    <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div>
                            <label className="mb-2 block font-semibold text-gray-700">Title</label>
                            <input name="title" value={formData.title} onChange={handleChange} required className="w-full rounded-lg border border-gray-300 px-4 py-2" />
                        </div>
                        <div>
                            <label className="mb-2 block font-semibold text-gray-700">Status</label>
                            <select name="status" value={formData.status} onChange={handleChange} className="w-full rounded-lg border border-gray-300 px-4 py-2">
                                <option value="active">Active</option>
                                <option value="paused">Paused</option>
                                <option value="completed">Completed</option>
                                <option value="cancelled">Cancelled</option>
                            </select>
                        </div>
                        <div>
                            <label className="mb-2 block font-semibold text-gray-700">Goal Amount</label>
                            <input type="number" name="goal_amount" value={formData.goal_amount} onChange={handleChange} required className="w-full rounded-lg border border-gray-300 px-4 py-2" />
                        </div>
                        <div>
                            <label className="mb-2 block font-semibold text-gray-700">Current Amount</label>
                            <input type="number" name="current_amount" value={formData.current_amount} onChange={handleChange} className="w-full rounded-lg border border-gray-300 px-4 py-2" />
                        </div>
                        <div>
                            <label className="mb-2 block font-semibold text-gray-700">Start Date</label>
                            <input type="date" name="start_date" value={formData.start_date} onChange={handleChange} required className="w-full rounded-lg border border-gray-300 px-4 py-2" />
                        </div>
                        <div>
                            <label className="mb-2 block font-semibold text-gray-700">End Date</label>
                            <input type="date" name="end_date" value={formData.end_date} onChange={handleChange} required className="w-full rounded-lg border border-gray-300 px-4 py-2" />
                        </div>
                        <div className="md:col-span-2">
                            <label className="mb-2 block font-semibold text-gray-700">Campaign Image</label>
                            <input type="file" name="image" accept="image/*" onChange={handleChange} className="w-full rounded-lg border border-gray-300 px-4 py-2" />
                        </div>
                        <div className="md:col-span-2">
                            <label className="mb-2 block font-semibold text-gray-700">Description</label>
                            <textarea name="description" value={formData.description} onChange={handleChange} rows="4" required className="w-full rounded-lg border border-gray-300 px-4 py-2" />
                        </div>
                        <div className="md:col-span-2 flex gap-4">
                            <button type="submit" className="rounded-lg bg-emerald-600 px-6 py-2 text-white hover:bg-emerald-700">Save</button>
                            <button type="button" onClick={resetForm} className="rounded-lg bg-gray-400 px-6 py-2 text-white hover:bg-gray-500">Cancel</button>
                        </div>
                    </form>
                </div>
            )}

            {loading ? (
                <div className="py-12 text-center">
                    <div className="inline-block h-12 w-12 animate-spin rounded-full border-b-2 border-emerald-600"></div>
                </div>
            ) : (
                <div className="overflow-hidden rounded-lg bg-white shadow-lg">
                    <table className="w-full">
                        <thead className="bg-emerald-600 text-white">
                            <tr>
                                <th className="px-6 py-3 text-left">Title</th>
                                <th className="px-6 py-3 text-left">Goal</th>
                                <th className="px-6 py-3 text-left">Raised</th>
                                <th className="px-6 py-3 text-left">Image</th>
                                <th className="px-6 py-3 text-left">Status</th>
                                <th className="px-6 py-3 text-left">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {campaigns.map((campaign) => (
                                <tr key={campaign.id} className="border-b hover:bg-gray-50">
                                    <td className="px-6 py-4">{campaign.title}</td>
                                    <td className="px-6 py-4">PKR {Number(campaign.goal_amount || 0).toLocaleString()}</td>
                                    <td className="px-6 py-4">PKR {Number(campaign.current_amount || 0).toLocaleString()}</td>
                                    <td className="px-6 py-4">
                                        {campaign.image && (
                                            <img src={resolveMediaUrl(campaign.image)} alt={campaign.title} className="h-10 w-10 rounded object-cover" />
                                        )}
                                    </td>
                                    <td className="px-6 py-4">{campaign.status}</td>
                                    <td className="flex gap-3 px-6 py-4">
                                        <button onClick={() => handleEdit(campaign)} className="text-emerald-600 hover:text-emerald-800"><FaEdit /></button>
                                        <button onClick={() => handleDelete(campaign.id)} className="text-red-600 hover:text-red-800"><FaTrash /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default Campaigns;
